import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  KubernetesSecurityFormData,
  KubernetesSecurityPolicyPayload,
  KUBERNETES_SECURITY_INITIAL_VALUES,
} from './security.types';

/**
 * Type guard to check if data is KubernetesSecurityPolicyPayload
 */
function isKubernetesSecurityPolicyPayload(
  data: unknown
): data is KubernetesSecurityPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'restrictDefaultNamespace' in data
  );
}

/**
 * Hook to get initial values for Kubernetes Security Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useKubernetesSecurityInitialValues(
  policy?: TypedPolicy<unknown>
): KubernetesSecurityFormData {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_SECURITY_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesSecurityPolicyPayload(data)) {
      return KUBERNETES_SECURITY_INITIAL_VALUES;
    }

    return {
      restrictDefaultNamespace: data.restrictDefaultNamespace?.value ?? false,
      restrictSecrets: data.restrictSecrets?.value ?? false,
      enablePodSecurityConstraints:
        data.enablePodSecurityConstraints?.value ?? false,
      restrictPrivilegedContainers:
        data.privilegedContainers?.value?.enabled ?? false,
      restrictHostNamespace: data.hostNamespaces?.value?.enabled ?? false,
      restrictHostNetworkingPorts: data.hostPorts?.value?.enabled ?? false,
      hostNetwork: data.hostPorts?.value?.hostNetwork ?? false,
      hostPortMin: data.hostPorts?.value?.min ?? 0,
      hostPortMax: data.hostPorts?.value?.max ?? 0,
      restrictVolumeTypes: data.volumeTypes?.value?.enabled ?? false,
      allowedVolumeTypes: data.volumeTypes?.value?.allowedTypes ?? [],
      restrictHostFilesystemPaths: data.hostFilesystem?.value?.enabled ?? false,
      allowedHostPaths: data.hostFilesystem?.value?.allowedPaths ?? [],
      restrictDrivers: data.allowFlexVolumes?.value?.enabled ?? false,
      allowedFlexVolumeDrivers:
        data.allowFlexVolumes?.value?.allowedVolumes ?? [],
      requireReadOnlyRootFilesystem:
        data.readOnlyRootFileSystem?.value?.enabled ?? false,
      restrictUserAndGroupIds: data.users?.value?.enabled ?? false,
      runAsUserType: data.users?.value?.runAsUser?.type ?? 'RunAsAny',
      runAsUserRanges: data.users?.value?.runAsUser?.idrange ?? [],
      runAsGroupType: data.users?.value?.runAsGroup?.type ?? 'RunAsAny',
      runAsGroupRanges: data.users?.value?.runAsGroup?.idrange ?? [],
      supplementalGroupsType:
        data.users?.value?.supplementalGroups?.type ?? 'RunAsAny',
      supplementalGroupsRanges:
        data.users?.value?.supplementalGroups?.idrange ?? [],
      fsGroupType: data.users?.value?.fsGroups?.type ?? 'RunAsAny',
      fsGroupRanges: data.users?.value?.fsGroups?.idrange ?? [],
      restrictEscalationToRootPrivileges:
        data.allowPrivilegeEscalation?.value?.enabled ?? false,
      restrictLinuxCapabilities: data.capabilities?.value?.enabled ?? false,
      allowedCapabilities: data.capabilities?.value?.allowedCapabilities ?? [],
      requiredDropCapabilities:
        data.capabilities?.value?.requiredDropCapabilities ?? [],
      defaultAddCapabilities: [],
      restrictSeLinuxSecurityContext: data.selinux?.value?.enabled ?? false,
      seLinuxOptions: data.selinux?.value?.allowedCapabilities ?? [],
      restrictProcMountTypes: data.allowProcMount?.value?.enabled ?? false,
      restrictAppArmorProfiles: data.appArmor?.value?.enabled ?? false,
      allowedAppArmorProfiles: data.appArmor?.value?.AppArmorType ?? [],
      restrictSeccompProfiles: data.secComp?.value?.enabled ?? false,
      allowedSeccompProfiles: data.secComp?.value?.secCompType ?? [],
      restrictSysctlProfiles:
        data.forbiddenSysctlsList?.value?.enabled ?? false,
      forbiddenSysctls:
        data.forbiddenSysctlsList?.value?.requiredDropCapabilities ?? [],
    };
  }, [policy]);
}
